import {
  getWorkflowAssigneeOptions,
  getWorkflowPlaceholderOptions,
  getWorkflowTaskPlaceholderOptions,
  PlaceholderOption,
} from "@/components/rich-editor/rich-editor.types.ts";
import { buildWorkflowPlaceholderOptions } from "@/pages/workflows/edit/actions/workflow-placeholder.helpers.ts";
import { useAppConfig } from "@/queries/settings/use-app.query.ts";
import { EmployeeColumnEntity } from "@/types/employees/employee-column.types.ts";
import { useEmployeeSearchColumns } from "@/queries/employees/use-employees.query.ts";
import {
  ApiWorkflowEventTypes,
  isCandidateWorkflowEventType,
} from "@/types/workflows/workflow.base-enum.types.ts";
import {
  WorkflowPlaceholderGroupTitles,
  WorkflowPlaceholderSubject,
} from "@/types/workflows/workflow-placeholder.types.ts";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

export const useWorkflowPlaceholderOptions = (
  eventType: ApiWorkflowEventTypes | null | undefined,
  subjects: readonly WorkflowPlaceholderSubject[]
): PlaceholderOption[] => {
  const { t } = useTranslation();
  const isCandidateWorkflow = isCandidateWorkflowEventType(eventType);
  const needsEmployeeCatalog = !isCandidateWorkflow || subjects.includes("assignee");
  const { data: columns } = useEmployeeSearchColumns(needsEmployeeCatalog);

  const { data: appConfig } = useAppConfig(needsEmployeeCatalog);
  const tableTitleById = useMemo(() => {
    const titles = new Map<number, string>();
    appConfig?.ui.employee_profile.tabs.forEach(({ items }) =>
      items.forEach(({ table }) => {
        if (table) titles.set(table.id, table.title);
      })
    );
    return titles;
  }, [appConfig]);

  return useMemo(() => {
    const groupTitles: WorkflowPlaceholderGroupTitles = {
      subjectTitles: {
        employee: t("core:employee"),
        assignee: t("workflow:variable_group_assignee"),
        manager: t("core:manager"),
      },
      entityTitles: {
        [EmployeeColumnEntity.personal]: t("core:employee"),
        [EmployeeColumnEntity.position]: t("core:position"),
        [EmployeeColumnEntity.employment]: t("core:employment"),
        [EmployeeColumnEntity.termination]: t("core:termination"),
        [EmployeeColumnEntity.custom_field]: t("custom_fields:custom_fields"),
        [EmployeeColumnEntity.custom_table]: t("custom_fields:custom_tables"),
      },
    };
    if (isCandidateWorkflow) {
      const candidateOptions = getWorkflowPlaceholderOptions(eventType);
      if (!subjects.includes("assignee")) return candidateOptions;
      return [
        ...buildWorkflowPlaceholderOptions(
          columns ?? [],
          ["assignee"],
          getWorkflowTaskPlaceholderOptions(),
          groupTitles,
          tableTitleById
        ),
        ...candidateOptions,
      ];
    }

    return buildWorkflowPlaceholderOptions(
      columns ?? [],
      subjects,
      [...getWorkflowPlaceholderOptions(eventType), ...getWorkflowAssigneeOptions()],
      groupTitles,
      tableTitleById
    );
  }, [columns, eventType, isCandidateWorkflow, subjects, t, tableTitleById]);
};
